import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
// Import from 'custom-element' for synchronous functions.
// See https://github.com/duetds/date-picker/issues/22
import { defineCustomElements } from '@duetds/date-picker/custom-element';
import moment from 'moment';
import clsx from 'clsx';
import * as styles from './Datepicker.module.scss';
import { useFormInputDatePickerLocalization } from './useFormInputDatePickerLocalization';
function hasDatePicker() {
    return !!customElements.get('duet-date-picker');
}
const ISO_8601_FORMAT = 'YYYY-MM-DD';
export { ISO_8601_FORMAT as DATE_FORMAT };
export function setupDatePicker() {
    if (!hasDatePicker()) {
        defineCustomElements(window);
        const HTMLDuetDatePickerConstructor = customElements.get('duet-date-picker');
        const originalShow = HTMLDuetDatePickerConstructor.prototype.show;
        HTMLDuetDatePickerConstructor.prototype.show = async function show(...args) {
            const show = await originalShow.apply(this, ...args);
            if (!this.value && this.max) {
                const maxValue = moment(this.max, ISO_8601_FORMAT).subtract(1, 'year');
                if (!this.min || maxValue.isAfter(moment(this.min, ISO_8601_FORMAT))) {
                    // @ts-expect-error setFocusedDay is private
                    this.setFocusedDay?.(maxValue.toDate());
                }
            }
            return show;
        };
    }
}
function useDOMListener(ref, eventName, handler) {
    const htmlHandler = handler;
    useEffect(() => {
        if (ref.current) {
            const element = ref.current;
            element.addEventListener(eventName, htmlHandler);
            return () => element.removeEventListener(eventName, htmlHandler);
        }
    }, [eventName, htmlHandler, ref]);
}
const createDateAdapter = (format) => ({
    parse(input) {
        const parsed = moment(input, format, /* strict */ true);
        if (parsed.isValid()) {
            return parsed.toDate();
        }
        return undefined;
    },
    format(date) {
        return moment(date).format(format);
    },
});
const Name = 'DatePicker';
export const DatePicker = ({ id, className, disabled, hasError, value, max, min, onChange, onFocus, onBlur, onOpen, onClose, ...props }) => {
    const ref = useRef(null);
    const [datePickerKey, setDatePickerKey] = useState('');
    const { firstDayOfWeek, format, localization } = useFormInputDatePickerLocalization();
    const updateDatePicker = useCallback(() => {
        if (!ref.current)
            return;
        if (localization)
            ref.current.localization = localization;
    }, [localization]);
    useEffect(() => {
        updateDatePicker();
    }, [updateDatePicker]);
    useEffect(() => {
        if (hasError) {
            ref.current?.classList.add('error');
        }
        else {
            ref.current?.classList.remove('error');
        }
    }, [hasError]);
    useDOMListener(ref, 'duetChange', (event) => {
        if (!event.detail.value) {
            onChange?.(undefined);
            return;
        }
        let selectedDate = moment(event.detail.valueAsDate);
        const maxMoment = max ? moment(max, ISO_8601_FORMAT) : null;
        if (maxMoment && selectedDate.isAfter(maxMoment)) {
            selectedDate = selectedDate.set('year', maxMoment.year());
            if (selectedDate.isAfter(maxMoment)) {
                selectedDate = selectedDate.subtract(1, 'year');
            }
            if (ref.current) {
                // we force rerrender to make sure displayed value is correct for user
                // e.g we have maxMoment 02/02/2022, user set 02/02/2022 and change to 02/02/2025
                // in onChange we receive right value but in input there will be 02/02/2025 because we manually change to 02/02/2022 and input won't refresh
                setDatePickerKey(key => key + 1);
                ref.current.value = selectedDate.format(ISO_8601_FORMAT);
            }
        }
        onChange?.(event);
    });
    useDOMListener(ref, 'duetFocus', (...args) => void onFocus?.(...args));
    useDOMListener(ref, 'duetBlur', (...args) => void onBlur?.(...args));
    useDOMListener(ref, 'duetOpen', (...args) => void onOpen?.(...args));
    useDOMListener(ref, 'duetClose', (...args) => void onClose?.(...args));
    if (!hasDatePicker()) {
        throw new Error('setupDatePicker() must be called before using the DatePicker component.');
    }
    const fieldStyles = clsx(styles.datePicker, className);
    const maxISOString = max ? moment(max).toISOString() : '';
    const minISOString = min ? moment(min).toISOString() : '';
    return (_jsx("duet-date-picker", { 
        // @ts-expect-error Custom web components use class={} instead of className={}
        // https://github.com/facebook/react/issues/4933#issuecomment-256664482
        class: fieldStyles, disabled: disabled, identifier: id, "data-component": Name, ref: element => {
            ref.current = element;
            if (ref.current) {
                ref.current.dateAdapter = createDateAdapter(format);
                if (hasError)
                    ref.current.classList.add('error');
                if (value)
                    ref.current.value =
                        typeof value === 'string'
                            ? value
                            : value?.format(ISO_8601_FORMAT);
                updateDatePicker();
            }
        }, firstDayOfWeek: firstDayOfWeek, max: maxISOString, min: minISOString, ...props }, datePickerKey));
};
